/**
 * The frontend source seam (plan §5): the media UI talks only to `RagoSource`.
 * `DemoSource` implements it over the static manifest today; `ServerSource`
 * implements it over the rago-server API (§4c) later — the UI never changes.
 * Item shapes mirror §4c JSON so that migration is mechanical.
 */

export interface ImageVariant {
  width: number;
  url: string;
}

/** One probed media stream (server sources; drives the track menus). */
export interface StreamInfo {
  index: number;
  type: "video" | "audio" | "subtitle";
  codec: string;
  language: string;
  channels: number;
  width: number;
  height: number;
  /** bits per second, as on the wire. */
  bitrate: number;
  frameRate: number;
  default: boolean;
  forced: boolean;
  /** SDH/CC track (embedded disposition or sidecar filename flag). */
  hearingImpaired?: boolean;
  /** Sidecar subtitle file registered as a stream (synthetic index >= 1000). */
  external?: boolean;
  /** Managed-download provenance ("opensubtitles", index >= 2000); absent otherwise. */
  source?: string;
  title: string;
  /** Encoding facts for Get Info (video streams; absent when the probe didn't know). */
  profile?: string;
  level?: number;
  bitDepth?: number;
  /** ffprobe transfer characteristics: smpte2084 = HDR10/PQ, arib-std-b67 = HLG. */
  colorTransfer?: string;
  colorPrimaries?: string;
  colorSpace?: string;
  /** ISOBMFF sample entry of a video stream (hvc1, dvh1, …); absent for Matroska. */
  codecTag?: string;
  /** ffprobe sample range (tv, pc) and pixel format (yuv420p10le) of a video stream. */
  colorRange?: string;
  pixelFormat?: string;
  /** Probe v3 facts (servers ≥ 2026-09-17; absent before the backfill).
   * Video: chroma siting (topleft), coded frame size, reference frames,
   * field order as ffprobe prints it (absent = unknown, never
   * progressive), sample aspect ratio ("1:1"). Audio: sample rate in Hz
   * and ffprobe's channel layout name ("5.1(side)"). */
  chromaLocation?: string;
  codedWidth?: number;
  codedHeight?: number;
  refFrames?: number;
  fieldOrder?: string;
  sampleAspectRatio?: string;
  sampleRate?: number;
  channelLayout?: string;
  /** Range type in the server's vocabulary (SDR, HDR10, HDR10Plus, HLG, DOVI, DOVIWithHDR10, …). */
  videoRange?: string;
  hdr10Plus?: boolean;
  dolbyVision?: {
    profile: number;
    level: number;
    blCompatId: number;
    rpuPresent: boolean;
    elPresent: boolean;
    blPresent: boolean;
  };
}

/** Episode hierarchy fields; presence marks an item as an episode. */
export interface EpisodeInfo {
  /** Episode number within the season. */
  index: number | null;
  /** Season item id. */
  parentId: string | null;
  /** Season number. */
  parentIndex: number | null;
  /** Show item id. */
  grandparentId: string | null;
  grandparentTitle: string | null;
  /** The show's art presence (server sources; absent on Discovery and on
   * servers predating the field) — rails wear the show's poster. */
  grandparentImages?: { poster: boolean };
}

export interface MediaItem {
  id: string;
  sourceId: string;
  guid: string;
  /** The wire's discriminator — one card/detail component branches on this. */
  type: "movie" | "show" | "season" | "episode";
  title: string;
  sortTitle: string;
  year: number;
  summary: string;
  genres: string[];
  runtimeMs: number;
  /** Provider facts on servers that keep them: studio, ISO release date,
   * the matched provider's own 0–10 score and every provider's. */
  studio?: string;
  releaseDate?: string | null;
  rating?: number | null;
  ratings?: Array<{ provider: string; value: number; votes?: number }>;
  externalIds: Array<{ provider: string; id: string }>;
  attribution?: {
    holder: string;
    license: string;
    licenseUrl: string;
    sourceUrl: string;
  };
  addedAt: string;
  /** Server items: last metadata write (ISO). Image URLs carry it as a
   * cache key so a Fix Match / refresh shows its new art at once. */
  updatedAt?: string;
  images: {
    poster: ImageVariant[];
    backdrop: ImageVariant[];
    /** Episode stills (server sources). */
    thumb?: ImageVariant[];
  };
  /** Marketing one-liner ("In space no one can hear you scream."). */
  tagline?: string;
  /** "PG-13", "TV-MA", … */
  contentRating?: string;
  /** Shows only: number of seasons (Rago Discovery). null/undefined for movies. */
  seasonCount?: number | null;
  /** Season or episode number within its parent. */
  index?: number | null;
  /** Episodes: first-air date (ISO). */
  airDate?: string | null;
  /** Shows: season count. Seasons: episode count. */
  childCount?: number | null;
  /** Seasons: the show. Episodes: the season. */
  parentId?: string | null;
  /** Playable server items: physical info for the quality menu (the first version). */
  mediaInfo?: MediaInfo | null;
  /** Every media version of a server item, in server order — mediaInfo is
   * versions[0]. The Discovery page lists one "watch from" card per version. */
  versions?: MediaInfo[];
  /**
   * First part's probed streams (server sources). For the ACTIVE playback's
   * subtitle menu prefer PlaybackInfo.subtitles — the decision picks the
   * part server-side and multi-version items may differ.
   */
  streams?: StreamInfo[];
  /** Present iff this item is an episode. */
  episode?: EpisodeInfo | null;
  /**
   * Continue Watching rows and next-up answers only (server sources; absent
   * elsewhere): playedAt = the play that put the row on deck (ms; the show's
   * most recent episode play for a next-up suggestion, the item's own for a
   * resume; null for a never-played show's first episode), resume = the row
   * IS that play rather than the episode after it.
   */
  onDeck?: { playedAt: number | null; resume: boolean } | null;
}

export interface UserItemState {
  watched: boolean;
  viewOffsetMs: number;
  viewCount: number;
  lastPlayedAt: number | null;
  /** Pre-selected audio stream index (detail page); null = auto. */
  audioStreamIndex: number | null;
  /** Pre-selected subtitle stream index; -1 = explicitly Off, null = unset. */
  subtitleStreamIndex: number | null;
}

export interface LibrarySettings {
  visibility: "default" | "excludeHome" | "excludeHomeSearch";
  useOriginalTitles: boolean;
  episodeSorting: "oldest" | "newest";
  useLocalAssets?: boolean;
  readNfo?: boolean;
}

export interface Library {
  id: string;
  sourceId: string;
  type: "movie" | "show";
  title: string;
  /** The primary metadata source (providers[0]) — what older servers report alone (§4d). */
  agent?: "tmdb" | "tvdb";
  /** Metadata sources in priority order; absent on servers that predate the chain. */
  providers?: Array<"tmdb" | "tvdb">;
  /** BCP-47 metadata language for this library. */
  language?: string;
  /** Live top-level item count (movies or shows). */
  itemCount?: number;
  /** Movies/shows the provider chain never matched — the unmatched queue's size. */
  unmatchedCount?: number;
  /** Absolute server paths — present for the owner only. */
  locations?: string[];
  settings?: LibrarySettings;
}

export interface Hub {
  id: string;
  title: string;
  items: MediaItem[];
  /** Per-library hubs (newer servers, id "recently-added:{libraryId}").
   * Absent on legacy 2-hub servers and on Discovery — feature-detect by
   * presence, never version-sniff. */
  libraryId?: string;
  libraryTitle?: string;
  libraryType?: "movie" | "show";
}

export interface SubtitleTrack {
  lang: string;
  label: string;
  url: string;
  /** Absolute ffprobe stream index (server sidecars; demo tracks omit it). */
  streamIndex?: number;
  /** SDH/CC track — the menu decorates the display name with it. */
  hearingImpaired?: boolean;
  /** External sidecar file, not a container stream. */
  external?: boolean;
  /** Managed-download provenance ("opensubtitles"); absent otherwise. */
  source?: string;
  codec?: string;
  forced?: boolean;
}

/** The server's encode recipe when a transcode session runs (§4c target). */
export interface TranscodeTarget {
  /** "copy" = Direct Stream (remux). */
  videoCodec: string;
  width?: number | null;
  height?: number | null;
  videoBitrateKbps?: number | null;
  toneMap?: boolean;
  audioCodec?: string | null;
  audioChannels?: number | null;
}

/**
 * A quality limit the server held a decision to, whatever the request
 * asked for (§4c limit). "relay": the request reached the server through
 * the relay.
 */
export interface PlaybackLimit {
  reason: "relay";
  maxResolution: number;
  maxBitrateKbps: number;
}

/** First media version's physical info (server sources; drives the quality menu). */
export interface MediaInfo {
  mediaId: string | null;
  container: string | null;
  videoCodec: string | null;
  audioCodec: string | null;
  bitrateKbps: number | null;
  width: number | null;
  height: number | null;
  /** Sum of the version's part file sizes. */
  sizeBytes: number | null;
  /** First part's id — the media-deletion target (server sources). */
  partId?: string | null;
  /** First part's file name (base name only; paths never leave the server). */
  fileName?: string | null;
  /** First part's absolute path — the owner's item detail only (Get Info). */
  filePath?: string | null;
  durationMs?: number | null;
  /** This version's first-part streams (Get Info lists every version). */
  streams?: StreamInfo[];
}

/** Per-playback knobs forwarded as DecisionRequest constraints (§4c). */
export interface PlaybackOptions {
  maxBitrateKbps?: number;
  maxResolution?: number;
  audioStreamIndex?: number;
  subtitleStreamIndex?: number;
  subtitleMode?: "burn" | "sidecar" | "embed";
  /** Direct Play / Direct Stream allowed (absent = yes). */
  directPlay?: boolean;
  directStream?: boolean;
  /** This browser's decoder failed on the item's direct video — the
   * server transcodes and says so (reason client.video.decode.failed). */
  decodeFailed?: boolean;
  /** Session start offset (resume / quality-switch position). */
  startMs?: number;
}

export interface PlaybackVerdict {
  video: "directplay" | "copy" | "transcode";
  audio: "directplay" | "copy" | "transcode";
  subtitle: "none" | "sidecar" | "embed" | "burn";
  reasons: string[];
}

/**
 * Optional timeline-report detail feeding the server dashboard's session
 * cards: who's playing (player identity), over what (connection kind),
 * and exactly which part/tracks — Direct Play is otherwise a black box
 * server-side. Sent on every beat; the server preserves last-known
 * values across beats that omit them.
 */
export interface TimelineExtras {
  player?: { product: string; name?: string; platform?: string; version?: string };
  connection?: "local" | "remote" | "relay";
  partId?: string;
  audioStreamIndex?: number;
  /** -1 = explicitly off (the persistence convention). */
  subtitleStreamIndex?: number;
}

/** One container chapter of a media part (GET /v1/parts/{id}/analysis). */
export interface Chapter {
  /** 0-based container order; the still's id. */
  index: number;
  startMs: number;
  endMs: number;
  /** "" when the container names nothing. */
  title: string;
  /** A still exists for chapterThumbUrl. */
  thumb: boolean;
}

export type MarkerType = "intro" | "credits";

/** A skip span: the opening a season's episodes share, or the closing
 * credits (final = the last credits span, after any mid-credits scene). */
export interface Marker {
  type: MarkerType;
  startMs: number;
  endMs: number;
  final: boolean;
  source: string;
  version: number;
}

/** Layout of the scrubber preview sheets: frames every intervalMs, width ×
 * height each, packed row-major tileCols × tileRows per sheet. */
export interface PreviewIndex {
  intervalMs: number;
  width: number;
  height: number;
  tileCols: number;
  tileRows: number;
  /** Thumbnails, not sheets. */
  count: number;
}

export interface PartAnalysis {
  chapters: Chapter[];
  markers: Marker[];
  /** null until preview thumbnails were generated for the part. */
  previews: PreviewIndex | null;
}

export interface PlaybackInfo {
  hlsMasterUrl: string | null;
  directUrl: string;
  durationMs: number;
  subtitles: SubtitleTrack[];
  /** Server sources: why the server chose this delivery. */
  verdict?: PlaybackVerdict;
  decisionId?: string;
  /** The media part the decision serves — for timeline reporting. */
  partId?: string;
  /** Present iff a transcode session was created (DELETE it on stop). */
  sessionId?: string;
  /**
   * The snapped position the session actually starts at (segment
   * boundary ≤ the requested startMs) — begin playback here, it is the
   * start of the buffered range.
   */
  startedAtMs?: number;
  /** Server sources: the encode recipe when converting ("720p, 3 Mbps"). */
  target?: TranscodeTarget | null;
  /** Server sources: the limit the server held this decision to, if any. */
  limit?: PlaybackLimit | null;
  /**
   * Stream indexes of the embedded text tracks the HLS session delivers as
   * subtitle renditions (at most three: the chosen one, then the file's
   * text tracks in stream order). A pick among them switches inside the
   * session; any other embedded text track needs a new session.
   */
  streamSubtitles?: number[];
}

/** Library browser sort keys (rago-server §4c; unknown keys fall back to title server-side). */
export const LIBRARY_SORTS = [
  "title",
  "added",
  "year",
  "released",
  "played",
  "playCount",
  "userRating",
  "rating",
  "contentRating",
  "duration",
  "resolution",
  "bitrate",
  "random",
  "lastEpisodeAdded",
] as const;
export type LibrarySort = (typeof LIBRARY_SORTS)[number];
/** The older name of LibrarySort. */
export type SortKey = LibrarySort;
export type SortOrder = "asc" | "desc";

/**
 * One library page's query: sort + direction, then the filter vocabulary
 * (lists are any-of within a facet and AND across facets), then the
 * window.
 */
export interface LibraryItemsQuery {
  sort: LibrarySort;
  /** Absent = the key's natural direction. */
  order?: SortOrder;
  /** sort=random: the shuffle is a pure function of (item, seed). */
  seed?: number;
  /** Title contains (matched against the folded sort title too). */
  q?: string;
  unmatched?: boolean;
  unwatched?: boolean;
  inProgress?: boolean;
  genre?: string[];
  year?: string[];
  decade?: string[];
  contentRating?: string[];
  studio?: string[];
  resolution?: string[];
  videoRange?: string[];
  audioLanguage?: string[];
  subtitleLanguage?: string[];
  offset?: number;
  limit?: number;
}

/** The filter half of LibraryItemsQuery — what the letter buckets take. */
export type LibraryFilterQuery = Omit<LibraryItemsQuery, "sort" | "order" | "seed" | "offset" | "limit">;

/** One A–Z rail entry: "#" (sorts before a), a letter, or "~" (other scripts, after z). */
export interface LetterBucket {
  key: string;
  count: number;
}

export interface FacetValue {
  value: string;
  count: number;
}

/** Every filter value a library carries, with counts (library-wide, never narrowed). */
export interface LibraryFacets {
  genres: FacetValue[];
  years: Array<{ value: number; count: number }>;
  contentRatings: FacetValue[];
  resolutions: FacetValue[];
  videoRanges: FacetValue[];
  audioLanguages: FacetValue[];
  subtitleLanguages: FacetValue[];
  studios: FacetValue[];
  unmatched: number;
  unplayed: number;
}

export interface LibraryPage {
  items: MediaItem[];
  /** Items in the whole (filtered) library — the virtual grid's slot count. */
  total: number;
}

export interface RagoSource {
  id: string;
  kind: "demo" | "server" | "discovery";
  name: string;
  getLibraries(): Promise<Library[]>;
  /** unmatched narrows to the provider-unmatched items (server sources; the Fix Match worklist). */
  /** One window of a library, Plex-style: the grid asks for the pages on
   * screen (offset/limit, ≤200) and sizes itself from `total`. */
  getLibraryItems(libraryId: string, q: LibraryItemsQuery): Promise<LibraryPage>;
  /** Items per initial under the same filters — the A–Z rail's offsets.
   * undefined = the server predates the route (feature-detect). Server sources only. */
  getLibraryLetters?(libraryId: string, q: LibraryFilterQuery): Promise<LetterBucket[] | undefined>;
  /** The filter popup's values with counts; undefined = older server. Server sources only. */
  getLibraryFacets?(libraryId: string): Promise<LibraryFacets | undefined>;
  getItem(itemId: string): Promise<MediaItem | null>;
  /** The item detail exactly as the server sent it — Get Info's "View JSON"
   * (Plex's View XML). Server sources only. */
  getItemRaw?(itemId: string): Promise<unknown>;
  getHomeHubs(): Promise<Hub[]>;
  search(query: string): Promise<MediaItem[]>;
  /**
   * "Do you have GUID X" (ARCHITECTURE §4b — availability stays inverted):
   * every copy of a canonical rago:// guid this account can reach here,
   * all media versions attached; with season+episode the guid is a show's
   * and the rows are that episode. Server sources only.
   */
  lookupItems?(guid: string, opts?: { season: number; episode: number }): Promise<MediaItem[]>;
  /**
   * The same question for up to 40 guids in one round trip: every asked
   * guid a key ([] when nothing is held); null when the server predates the
   * batch form and the caller should fall back to lookupItems per guid.
   * Server sources only.
   */
  lookupItemsBatch?(guids: string[]): Promise<Map<string, MediaItem[]> | null>;
  imageUrl(item: MediaItem, kind: "poster" | "backdrop" | "thumb", minWidth: number): string;
  /** Hierarchy children (show→seasons, season→episodes) as full items.
   * Server sources only; leaves yield an empty list. */
  getChildren?(itemId: string): Promise<MediaItem[]>;
  /**
   * The show/season page's Play target by the server's Continue Watching
   * rule (Plex On Deck). null = nothing unwatched after the last play;
   * undefined = the server predates the route (feature-detect) — walk the
   * hierarchy locally instead. Server sources only.
   */
  getNextUp?(rootId: string): Promise<MediaItem | null | undefined>;
  /** Artwork straight from an item id — for surfaces with no MediaItem in hand. */
  childImageUrl?(childId: string, kind: "poster" | "thumb", width: number): string;
  /**
   * Chapters, skip markers and the scrubber preview index of one media
   * part (timings attach to the file, never the logical item). undefined =
   * the server predates media analysis (404) — feature-detect, never
   * version-sniff. Server sources only.
   */
  getPartAnalysis?(partId: string): Promise<PartAnalysis | undefined>;
  /** One preview sheet's URL (imageUrl idiom: "" while the token is cold —
   * callers subscribe via useSourceConnection and retry on the next render). */
  previewTileUrl?(partId: string, tile: number): string;
  /** One chapter still's URL (same idiom). */
  chapterThumbUrl?(partId: string, index: number, width: number): string;
  getPlayback(itemId: string, opts?: PlaybackOptions): Promise<PlaybackInfo>;
  /** Tear down server-side playback state (transcode session). Fire-and-forget. */
  stopPlayback?(info: PlaybackInfo): void;
  getUserState(itemId: string): UserItemState;
  reportProgress(
    itemId: string,
    state: "playing" | "paused" | "stopped" | "ended",
    timeMs: number,
    durationMs: number,
    sessionId?: string,
    extras?: TimelineExtras,
    /** Final/last-chance beats ride a keepalive fetch so the report
     * survives unmount and pagehide. */
    opts?: { keepalive?: boolean },
  ): void | Promise<void>;
  setWatched(itemId: string, watched: boolean): void;
  /**
   * Persist the caller's per-item track pre-selection (Plex detail-page
   * pattern). Partial: send only the field being changed. Server sources
   * only; optimistic, fire-and-forget.
   */
  setStreamSelection?(
    itemId: string,
    sel: { audioStreamIndex?: number | null; subtitleStreamIndex?: number | null },
  ): void;
}
